<template>
  <Navbar>
    <template v-slot:navbarTitle>
      <h1 class="h5 m-0">{{ selectedInspectionPlan.product_model }}</h1>
    </template>
    <template v-slot:navbarButton>
      <router-link
        :to="{
          name: 'AppHomeOperate',
        }"
        class="btn btn-dark"
        ><i class="bi bi-0-square me-1"></i>戻る</router-link
      >
    </template>
  </Navbar>

  <main id="main" class="bg-light">
    <div v-if="!isLoading" class="container py-3 py-lg-4">
      <h5 class="text-truncate mb-3">
        <i class="bi bi-clipboard me-1"></i
        >{{ selectedInspectionPlan.inspection_type_name }} /
        {{ selectedInspectionPlan.inspection_name }}
      </h5>
      <template
        v-if="
          inspection.inspection_images &&
          inspection.inspection_images.length > 0
        "
      >
        <ul class="nav nav-tabs">
          <li class="nav-item" v-for="(tab, idx) in imageTabs" :key="idx">
            <a
              class="nav-link"
              :class="{ active: activeImageTab === idx }"
              href="#"
              @click.prevent="activeImageTab = idx"
              >ページ{{ idx + 1 }}</a
            >
          </li>
        </ul>
        <div
          class="row row-cols-2 row-cols-md-3 g-1 g-md-2 p-2 bg-white m-0 border border-top-0"
        >
          <ImageGallery
            :ref="'galleryRef' + activeImageTab"
            :files="getImagesByTab(activeImageTab)"
            thumbnail_class="object-fit-contain"
            @ready="setGalleryRef"
          >
            <template v-slot:image-placeholder>
              <div
                class="ratio ratio-16x9 bg-white border d-flex align-items-center justify-content-center"
              >
                画像はありません
              </div>
            </template>
            <template v-slot:thumbnail-operation-icons="{ file }">
              <div class="d-flex justify-content-start p-1">
                <button class="btn btn-outline-dark border-0">
                  <i class="bi" :class="getTabIconClass(file)"></i>
                </button>
              </div>
            </template>
          </ImageGallery>
        </div>
      </template>
      <div v-else class="row g-1 g-md-2">
        <div class="col-12">
          <p>画像の登録はありません</p>
        </div>
      </div>
      <div class="mt-2 d-flex justify-content-end">
        <div class="btn btn-lg btn-primary" @click="backToInspectionList()">
          <i class="bi bi-9-square me-1"></i>作業完了
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import inspectionStore from '@/apps/inspection/operate/store'
import ImageGallery from '@/components/AppImageGallery.vue'
import Navbar from '@/components/operate/OperateNavbar.vue'
import store from '../store'

export default {
  name: 'OperateInspectionMonitorDetail',
  components: {
    Navbar,
    ImageGallery,
  },

  props: {
    id: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      isLoading: true,
      imageTabs: [0], // 最初は1つ
      activeImageTab: 0,
      maxImagesPerTab: 6,
      position: null,
      positionTab: null,
      galleryRef: null,
    }
  },
  computed: {
    inspection() {
      return inspectionStore.getters['operate_inspection/detail'] || {}
    },
    selectedInspectionPlan() {
      return (
        store.getters['operate_inspection_plan/selected_inspection_plan'] || {}
      )
    },
  },

  watch: {
    'inspection.inspection_images': {
      immediate: true,
      handler(newVal) {
        const maxPosition = Math.max(
          0,
          ...(newVal || []).map((img) => img.position || 0),
        )
        const neededTabs = Math.floor(maxPosition / this.maxImagesPerTab) + 1
        this.imageTabs = Array.from({ length: neededTabs }, (_, i) => i)
      },
    },
    activeImageTab: {
      immediate: false,
      handler() {
        this.$nextTick(() => {
          this.setGalleryRef()
        })
      },
    },
  },

  async created() {
    try {
      await this.getData(this.id)
    } catch (error) {
      if (error.status) {
        window.setTimeout(() => {
          this.$router.push({
            name: 'AppHomeOperate',
          })
        }, this.$store.getters['alert/timeout'])
      }
    } finally {
      this.isLoading = false
    }
  },
  beforeRouteLeave(to, from, next) {
    // 一覧画面以外のページに移動する場合にデータをクリアする
    if (to.name !== 'OperateInspectionMonitor') {
      store.dispatch('operate_inspection_plan/clearAll')
    }
    next()
  },

  mounted() {
    window.addEventListener('keydown', this.handleKeyDown)
    this.$nextTick(() => {
      this.galleryRef = this.$refs['galleryRef' + this.activeImageTab]
    })
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeyDown)
  },

  methods: {
    async getData(id) {
      await inspectionStore.dispatch('operate_inspection/fetchData', id)
    },
    getImagesByTab(tabIdx) {
      if (!this.inspection.inspection_images) {
        // 空データ6つ
        return Array.from({ length: this.maxImagesPerTab }, (_, i) => ({
          _tabIndex: i,
        }))
      }
      const start = tabIdx * this.maxImagesPerTab
      const end = start + this.maxImagesPerTab
      // 指定範囲の画像をpositionでマップ
      const imagesMap = {}
      this.inspection.inspection_images.forEach((img) => {
        if (img.position >= start && img.position < end) {
          imagesMap[img.position - start] = {
            ...img,
            _tabIndex: img.position - start,
          }
        }
      })
      // 0〜5の各positionに画像がなければ空データ
      return Array.from(
        { length: this.maxImagesPerTab },
        (_, i) => imagesMap[i] || { _tabIndex: i },
      )
    },
    getTabIconClass(file) {
      if (file && file._tabIndex !== undefined) {
        return `bi-${file._tabIndex + 1}-square`
      }
      return ''
    },
    setGalleryRef() {
      this.galleryRef = this.$refs['galleryRef' + this.activeImageTab]
    },
    isLightBoxVisible() {
      return this.galleryRef && this.galleryRef.visible
    },
    backToInspectionList() {
      // 検査種別だけを残してほかのデータをクリア
      store.dispatch('operate_inspection_plan/clearData')
      this.$router.push({ name: 'OperateInspectionMonitor' })
    },
    handleKeyDown(event) {
      if (document.activeElement.tagName.toLowerCase() === 'input') {
        return
      }
      switch (event.key) {
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6': {
          if (!this.isLightBoxVisible()) {
            this.galleryRef.showLightBox(parseInt(event.key) - 1)
          }
          break
        }
        case '9':
          this.backToInspectionList()
          break
        case '0':
          if (!this.isLightBoxVisible()) {
            this.$router.push({ name: 'AppHomeOperate' })
          }
          break
        case 'Backspace': {
          if (this.isLightBoxVisible()) {
            this.galleryRef.hideLightBox()
          }
          break
        }
        case '+': {
          if (this.isLightBoxVisible()) {
            this.galleryRef.zoomIn()
          }
          break
        }
        case '-': {
          if (this.isLightBoxVisible()) {
            this.galleryRef.zoomOut()
          }
          break
        }
        // タブの切り替えは矢印キーで行う
        case 'ArrowRight':
          if (!this.isLightBoxVisible()) {
            this.activeImageTab =
              (this.activeImageTab + 1) % this.imageTabs.length
          }
          break
        case 'ArrowLeft':
          if (!this.isLightBoxVisible()) {
            this.activeImageTab =
              (this.activeImageTab - 1 + this.imageTabs.length) %
              this.imageTabs.length
          }
          break
        default:
          break
      }
    },
  },
}
</script>
